import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { AnswerForType } from "@tellescope/types-models";
import { useCallback, useMemo } from "react";
import { isSubmittableResponse, normalizeResponseForSubmission, subFieldIdsOf } from "../../utils";
import type { FormFieldNode } from "../../utils/types";
import type { useTellescopeFormCore } from "../use-tellescope-form-core/use-tellescope-form-core";

export interface UseSaveFieldResponseArgs {
  core: ReturnType<typeof useTellescopeFormCore>;
  responseId: string | undefined;
  responseAccessCode: string | undefined;
}

export const useSaveFieldResponse = ({ core, responseId, responseAccessCode }: UseSaveFieldResponseArgs) => {
  const preparedResponseId = responseId ?? core.prepareFormResponseMutation.data?.response.id;
  const accessCode = responseAccessCode ?? core.prepareFormResponseMutation.data?.response.accessCode;

  const canSave = !!preparedResponseId || !!accessCode;

  const target = useMemo(() => ({ formResponseId: preparedResponseId, accessCode }), [preparedResponseId, accessCode]);

  const saveActiveFieldAndSubFields = useCallback(
    (activeField: FormFieldNode) => {
      const currentResponse = core.currentResponses[activeField.value.id];
      if (!currentResponse || !canSave) return;

      core.saveFieldResponseMutation.mutate({ ...target, response: currentResponse });

      subFieldIdsOf(activeField.value).forEach((subFieldId) => {
        const subResponse = core.currentResponses[subFieldId];
        if (!subResponse) return;
        core.saveFieldResponseMutation.mutate({ ...target, response: subResponse });
      });
    },
    [core.currentResponses, core.saveFieldResponseMutation, canSave, target]
  );

  const saveChargebeeFieldResponse = useCallback(
    (field: TellescopeModelFromName<"form_fields">, value: AnswerForType["Chargebee"]) => {
      if (!canSave) return;

      core.saveFieldResponseMutation.mutate({
        ...target,
        response: {
          fieldId: field.id,
          fieldTitle: field.title ?? "",
          externalId: field.externalId,
          fieldDescription: field.description,
          fieldHtmlDescription: field.htmlDescription,
          answer: { type: "Chargebee", value },
        },
      });
    },
    [core.saveFieldResponseMutation, canSave, target]
  );

  const saveFieldResponse = useCallback(
    async (activeField: FormFieldNode | undefined) => {
      if (!activeField) return;

      const currentResponse = core.currentResponses[activeField.value.id];
      if (!currentResponse || !isSubmittableResponse(currentResponse)) return;

      if (!canSave) return;

      await core.saveFieldResponseMutation.mutateAsync({
        ...target,
        response: normalizeResponseForSubmission(currentResponse),
      });
    },
    [core.currentResponses, core.saveFieldResponseMutation, canSave, target]
  );

  return { saveActiveFieldAndSubFields, saveChargebeeFieldResponse, saveFieldResponse };
};
